import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { Loadable, Tabbar, TabPanel, useTabs, useLoad, fmt, Status, ActionButton, ActionDialog, Section, History } from "@/components/control/kit";
import { V3Page, V3Aside, Field, Pill, Notice, CheckRow, V3Actions, useRun, ErrorLine, FilterEmpty, TextArea } from "@/components/control/v3";

const STATUS_TEXT = { offen: "Offen", "zuständigkeit_geprüft": "Zuständigkeit geprüft", "bestätigt": "Bestätigt", abgelehnt: "Abgelehnt" };

export default function OrgClaims() {
  const [status, setStatus] = useState("open");
  const tabs = useTabs([{ value: "open", label: "Offen" }, { value: "all", label: "Alle" }], status);
  const st = useLoad(`/org-claims?status=${status}`);
  return (
    <V3Page crumb="Organisationen" title="Organisationseinträge beansprucht" subtitle="Ansprüche auf bestehende Organisationseinträge prüfen." testId="org-claims-page">
      <Tabbar tabs={tabs} onChange={setStatus} testId="org-claims-tabs" />
      <TabPanel tabs={tabs} testId="org-claims-panel">
        <Loadable state={st}>
          {(d) => (d.claims.length === 0 ? (
            <FilterEmpty filters={[["Status", status === "open" ? "Offen" : "Alle"], ["Bereich", "Organisationsansprüche"]]}
              onReset={status === "open" ? () => setStatus("all") : null} />
          ) : (
            <ul className="space-y-2.5" data-testid="org-claims-list">
              {d.claims.map((c) => (
                <li key={c.id}>
                  <Link to={`/organisationen/ansprueche/${c.id}`} data-testid={`org-claim-${c.id}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E6E1F0] bg-white px-4 py-3 transition-colors hover:border-brand-magenta">
                    <span><span className="block font-semibold text-brand-purple">{c.organization_name}</span>
                      <span className="text-sm text-zinc-600">{c.member_name} · eingereicht {fmt(c.created_at)}</span></span>
                    <Status value={c.status} />
                  </Link>
                </li>
              ))}
            </ul>
          ))}
        </Loadable>
      </TabPanel>
    </V3Page>
  );
}

function ClaimFields({ c, scope, lead }) {
  if (lead) {
    return (
      <>
        <Field label="Organisation" testId="claim-org">{c.organization_name}</Field>
        <Field label="Vorgeschlagene Leitung" testId="claim-member">{c.member_name}</Field>
        <Field label="Umfang">{scope}</Field>
        <Field label="Persönliche Passfreigaben">Nicht in der Organisationsrolle enthalten</Field>
      </>
    );
  }
  return (
    <>
      <Field label="Eintrag" testId="claim-org">{c.organization_name}</Field>
      <Field label="Antragstellerin" testId="claim-member">{c.member_name}</Field>
      <Field label="Nachweis" testId="claim-evidence">{c.evidence} · {c.status === "offen" ? "Zur Prüfung" : STATUS_TEXT[c.status]}</Field>
      <Field label="Prüfstatus" testId="claim-status">{STATUS_TEXT[c.status]}</Field>
    </>
  );
}

function ClaimDecision({ d, reload }) {
  const c = d.claim;
  const lead = c.status === "zuständigkeit_geprüft";
  const navigate = useNavigate();
  const [checks, setChecks] = useState({});
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const { busy, error, run } = useRun();
  const allChecked = Object.keys(d.checks).every((k) => checks[k]);
  const review = async () => {
    if (await run(() => api.post(`/org-claims/${c.id}/review`, { checks, reason }), "Zuständigkeit geprüft.")) reload();
  };
  return (
    <>
      <h2 className="pt-2 text-lg font-bold text-brand-purple">Prüfpunkte</h2>
      {Object.entries(d.checks).map(([k, l]) => (
        <CheckRow key={k} id={`claim-check-${k}`} label={l} checked={!!checks[k]} onChange={(v) => setChecks((s) => ({ ...s, [k]: v }))} />
      ))}
      {lead ? <Notice title="Wirkung nach Bestätigung" text="Der Vorgang bleibt bis zu deiner ausdrücklichen Bestätigung offen." testId="claim-effect" />
        : <TextArea id="claim-reason" label="Begründung" required value={reason} onChange={setReason} help="Wird im Verlauf gespeichert." />}
      {lead && <p className="text-sm text-zinc-600" data-testid="claim-reconfirm-hint">Die Erstprüfung ist gespeichert. Für die Bestätigung der Leitung die Prüfpunkte bitte ausdrücklich erneut bestätigen.</p>}
      {!allChecked && <p className="text-sm text-zinc-600" data-testid="claim-check-hint">Alle Prüfpunkte bestätigen, um fortzufahren.</p>}
      <ErrorLine error={error} testId="claim-error" />
      <V3Actions primary={lead ? "Bestätigung prüfen" : "Zuständigkeit prüfen"} primaryDisabled={!allChecked} busy={busy}
        onPrimary={lead ? () => setConfirm(true) : review} onSecondary={() => navigate("/organisationen/ansprueche")} testId="claim" />
      <ActionButton label="Anspruch ablehnen" icon={X} testId="reject-claim" danger submitLabel="Ablehnen" onDone={reload}
        description="Die antragstellende Person wird benachrichtigt. Es entsteht keine Organisationsrolle."
        fields={[{ name: "reason", label: "Begründung", type: "textarea", required: true }, { name: "message", label: "Nachricht an die Person", type: "textarea" }]}
        onSubmit={async (v) => { await api.post(`/org-claims/${c.id}/reject`, v); return "Anspruch abgelehnt."; }} />
      <ActionDialog open={confirm} onOpenChange={setConfirm} title="Organisationsleitung jetzt bestätigen" testId="confirm-lead-dialog" submitLabel="Jetzt bestätigen"
        description={`${c.member_name} wird Organisationsleitung für ${c.organization_name}.`} onDone={reload}
        fields={[{ name: "confirm", type: "checkbox", required: true, label: "Ich bestätige die Leitung mit begrenztem Umfang. Persönliche Passfreigaben sind nicht enthalten." },
          { name: "reason", label: "Begründung", type: "textarea", required: true }]}
        onSubmit={async (v) => { await api.post(`/org-claims/${c.id}/confirm-lead`, { reason: v.reason }); return "Organisationsleitung bestätigt."; }} />
    </>
  );
}

function ClaimView({ d, reload }) {
  const c = d.claim;
  const lead = c.status === "zuständigkeit_geprüft";
  const done = ["bestätigt", "abgelehnt"].includes(c.status);
  return (
    <V3Page crumb="Organisationen" testId="org-claim-detail"
      title={lead || c.status === "bestätigt" ? "Organisationsleitung bestätigen" : "Organisationseintrag beansprucht"}
      subtitle={lead ? "Die Bestätigung betrifft die Organisationszuständigkeit und ihren begrenzten Umfang." : "Prüfe den Anspruch auf einen bestehenden Organisationseintrag."}
      aside={<V3Aside subjectTitle="Ausgewählter Vorgang" subject={c.organization_name} />}>
      <div>{done ? <Pill tone={c.status === "bestätigt" ? "success" : "critical"} testId="claim-pill">{STATUS_TEXT[c.status]}</Pill>
        : <Pill testId="claim-pill">Vor der Entscheidung</Pill>}</div>
      <ClaimFields c={c} scope={d.scope} lead={lead || c.status === "bestätigt"} />
      {!done && <ClaimDecision key={c.status} d={d} reload={reload} />}
      {c.history.length > 0 && <Section title="Verlauf" testId="claim-history" className="mt-4"><History items={c.history} /></Section>}
    </V3Page>
  );
}

export function OrgClaimDetail() {
  const { id } = useParams();
  const st = useLoad(`/org-claims/${id}`);
  return <Loadable state={st}>{(d) => <ClaimView d={d} reload={st.reload} />}</Loadable>;
}
